import { Avatar, Box, Button, IconButton, Modal, Paper } from "@mui/material";
import makeStyles from '@mui/styles/makeStyles';
import { useCallback, useEffect, useRef, useState } from "react";
import CancelIcon from "@mui/icons-material/Cancel";
import Camera from "react-html5-camera-photo";
import ReactCrop from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import 'react-html5-camera-photo/build/css/index.css';

const useStyles = makeStyles((theme) => ({
  paper: {
    position: "absolute",
    width: "30%",
    backgroundColor: theme.palette.background.paper,
    border: "1px solid #000",
    borderRadius: 10,
    boxShadow: theme.shadows[5],
    padding: theme.spacing(2, 4, 3),
    top: `15%`,
    left: `35%`,
    maxWidth: "30%",
  },
  cameraPaper: {
    position: "absolute",
    width: "70%",
    backgroundColor: theme.palette.background.paper,
    border: "1px solid #000",
    borderRadius: 10,
    boxShadow: theme.shadows[5],
    padding: theme.spacing(2, 4, 3),
    top: `0%`,
    left: `15%`,
    display:"flex",
    justifyContent:"center",
  },
  button: {
    "&:hover": {
      backgroundColor: theme.palette.gif.blue.hover,
    },
    color: theme.palette.gif.blue.color,
    backgroundColor: theme.palette.gif.blue.background,
    marginTop: 10,
    height: 50,
  },
  saveButton: {
    "&:hover": {
      backgroundColor: theme.palette.gif.green.hover,
    },
    color: theme.palette.gif.green.color,
    backgroundColor: theme.palette.gif.green.background,
    marginTop: 10,
    height: 50,
  },
  photo: {
    width: "100%",
    height: "auto",
    // border:"black 1px solid"
    border: "1px solid #555",
  },
  closeIcon: {
    position:"absolute",
    float: "right",
    top:-10,
    right: -10,
  },
}));

export default function AddPhoto(props) {
  const classes = useStyles();
  const { open, onClose, uploadPhoto, image } = props;
  const [imgUrl, setImgUrl] = useState(null);
  const [imgFile, setImgFile] = useState(null);
  const [hasCamera, setHasCamera] = useState(false);
  const fileUploadRef = useRef(null);
  const imgRef = useRef(null);
  const previewCanvasRef = useRef(null);
  const [openCamera, setOpenCamera] = useState(false);
  const [completedCrop, setCompletedCrop] = useState(null);
  const [crop, setCrop] = useState({
    aspect: 1 / 1,
    unit: "%",
    width: 80,
    // height: 80,
    x: 10,
    y: 0,
  });

  function generateImage(canvas, crop) {
    if (!crop || !canvas) {
      return;
    }

    return new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          blob.name = "fileName";
          resolve(blob);
        },
        "image/jpeg",
        1
      );
    });
  }

  const onLoad = useCallback((img) => {
    imgRef.current = img;
  }, []);

  useEffect(() => {
    if (!completedCrop || !previewCanvasRef.current || !imgRef.current) {
      return;
    }

    const image = imgRef.current;
    const canvas = previewCanvasRef.current;
    const crop = completedCrop;

    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    const ctx = canvas.getContext("2d");
    const pixelRatio = window.devicePixelRatio;

    canvas.width = crop.width * pixelRatio;
    canvas.height = crop.height * pixelRatio;

    ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
    ctx.imageSmoothingQuality = "high";

    ctx.drawImage(
      image,
      crop.x * scaleX,
      crop.y * scaleY,
      crop.width * scaleX,
      crop.height * scaleY,
      0,
      0,
      crop.width,
      crop.height
    );
  }, [completedCrop]);

  const handleOnCrop = (crop) => {
    setCrop(crop);
  };

  const cropImage = async () => {
    var img = await generateImage(previewCanvasRef.current, completedCrop);
    uploadPhoto(img);
    setImgFile(null);
    setImgUrl(null);
    setCrop({
      aspect: 1 / 1,
      unit: "%",
      width: 80,
      // height: 80,
      x: 10,
      y: 0,
    });
  };

  const changeHandler = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    setImgFile(file);
    setImgUrl(URL.createObjectURL(file));
  };

  function handleTakePhoto(dataUri) {
    setImgUrl(dataUri);
    setOpenCamera(false)
  }

  useEffect(() => {
    if (!open) return;
    navigator.mediaDevices
      .getUserMedia({ video: true })
      .then(function (stream) {
        setHasCamera(true);//have camera
      })
      .catch(function (err) {
        setHasCamera(false);//dont have camera
      });
  }, [open]);

  return <>
    <Modal open={openCamera} onClose={() => setOpenCamera(false)}>
      <Paper className={classes.cameraPaper}>
        {openCamera&&<Camera
          idealResolution={{ width: 1000, height: 1000 }}
          onTakePhotoAnimationDone={(dataUri) => { handleTakePhoto(dataUri); }}
        />}
      </Paper>
    </Modal>
    <Modal open={open} onClose={onClose}>
      <Paper className={classes.paper}>
      <IconButton className={classes.closeIcon} onClick={onClose} size="large">
        <CancelIcon style={{ fontSize: 25 }} />
      </IconButton>
        {imgUrl ? (
          <>
            <ReactCrop
              imageStyle={{ width: "100%" }}
              style={{ width: "100%" }}
              src={imgUrl ? imgUrl : image}
              crop={crop}
              onChange={handleOnCrop}
              onImageLoaded={onLoad}
              onComplete={(c) => setCompletedCrop(c)}
            />
            <canvas
              ref={previewCanvasRef}
              // Rounding is important so the canvas width and height matches/is a multiple for sharpness.
              style={{
                display: "none",
                width: Math.round(completedCrop?.width ?? 0),
                height: Math.round(completedCrop?.height ?? 0),
              }}
            />
          </>
        ) : (
          <Avatar
            variant="rounded"
            src={imgUrl ? imgUrl : image}
            className={classes.photo}
          ></Avatar>
        )}
        <Box padding={1}>
          {hasCamera && (
            <Button
              fullWidth
              className={classes.button}
              onClick={() => setOpenCamera(true)}
            >
              Take a picture
            </Button>
          )}
          <input
            ref={fileUploadRef}
            accept="image/png, image/jpeg"
            onChange={changeHandler}
            type="file"
            style={{ display: "none" }}
          />
          <Button
            fullWidth
            className={classes.button}
            onClick={() => fileUploadRef.current.click()}
          >
            Upload picture
          </Button>
          {(imgFile||imgUrl) && (
            <Button fullWidth className={classes.saveButton} onClick={cropImage}>
              Save
            </Button>
          )}
        </Box>
      </Paper>
    </Modal>
  </>;
}
